<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

// Define props and emits for v-model
const props = withDefaults(defineProps<{
    modelValue?: string | Date | null;
    isMenu?: boolean | true | null;
}>(), {
    isMenu: true
});

const emit = defineEmits<{
    'update:modelValue': [value: string | null];
    change: [value: string | null];
}>();

// Get current locale and translation function from i18n
const { locale, t } = useI18n();

// Convert initial value to YYYY-MM-DD format
const getInitialValue = () => {
    if (!props.modelValue) return null;
    if (typeof props.modelValue === 'string') {
        // If it's already a string, return as is
        return props.modelValue;
    }
    // If it's a Date object, convert to YYYY-MM-DD
    return props.modelValue.toISOString().split('T')[0];
};

const model = ref(getInitialValue());
const dateMenu = ref(false);

// Calculate minimum allowed date (6 months ago from today)
const minDate = computed(() => {
    const today = new Date();
    const minAllowedDate = new Date(today);
    minAllowedDate.setMonth(today.getMonth() - 6);
    return minAllowedDate;
});

// Calculate maximum allowed date (7 days from today)
const maxDate = computed(() => {
    const today = new Date();
    const maxAllowedDate = new Date(today);
    maxAllowedDate.setDate(today.getDate() + 7);
    return maxAllowedDate;
});

// Watch for external changes to modelValue prop
watch(
    () => props.modelValue,
    (newValue) => {
        model.value = getInitialValue();
    },
);

// Watch for internal changes to model and emit events
watch(model, (newValue) => {
    // Always emit as YYYY-MM-DD string format
    emit('update:modelValue', newValue);
    emit('change', newValue);
});

// Date formatting function for display using manual translations
const formatDate = (dateString: string | null) => {
    if (!dateString) return '';

    // Parse the YYYY-MM-DD string to create a proper date
    const date = new Date(dateString + 'T00:00:00'); // Add time to avoid timezone issues

    // Get day name from translations
    const dayNames = [
        t('date.days.sunday'),
        t('date.days.monday'),
        t('date.days.tuesday'),
        t('date.days.wednesday'),
        t('date.days.thursday'),
        t('date.days.friday'),
        t('date.days.saturday')
    ];

    // Get month name from translations
    const monthNames = [
        t('date.monthsFull.january'),
        t('date.monthsFull.february'),
        t('date.monthsFull.march'),
        t('date.monthsFull.april'),
        t('date.monthsFull.may'),
        t('date.monthsFull.june'),
        t('date.monthsFull.july'),
        t('date.monthsFull.august'),
        t('date.monthsFull.september'),
        t('date.monthsFull.october'),
        t('date.monthsFull.november'),
        t('date.monthsFull.december')
    ];

    const dayName = dayNames[date.getDay()];
    const day = String(date.getDate()).padStart(2, '0');
    const month = monthNames[date.getMonth()];
    // return `${dayName}, ${day} ${month} `;
    return `${day} ${month} `;
};

const formattedDate = computed(() => {
    return formatDate(model.value);
});

// Handle date picker change - convert Date object to YYYY-MM-DD string
const handleDateChange = (dateValue: Date | string | null) => {
    if (dateValue instanceof Date) {
        // Convert Date object to YYYY-MM-DD format
        const year = dateValue.getFullYear();
        const month = String(dateValue.getMonth() + 1).padStart(2, '0');
        const day = String(dateValue.getDate()).padStart(2, '0');
        model.value = `${year}-${month}-${day}`;
    } else {
        model.value = dateValue;
    }
    dateMenu.value = false;
};
</script>
<template>
    <div class="fs-form-field fs-date-input" v-if="props.isMenu">
        <div class="d-flex justify-center">
            <v-menu v-model="dateMenu" :close-on-content-click="false" location="bottom center">
                <template v-slot:activator="{ props }">
                    <v-text-field v-bind="props" :model-value="formattedDate" :label="t('date.label')" readonly
                        focus="true"></v-text-field>
                </template>
                <v-locale-provider :locale="locale">
                    <v-date-picker :title="t('vuetify.datePicker.title')"
                        :model-value="model ? new Date(model + 'T00:00:00') : null" color="primary"
                        @update:model-value="handleDateChange"></v-date-picker>
                </v-locale-provider>
            </v-menu>
        </div>
    </div>
    <v-locale-provider :locale="locale" v-else>
        <v-date-picker :title="t('vuetify.datePicker.title')"
            :model-value="model ? new Date(model + 'T00:00:00') : null" color="primary"
            @update:model-value="handleDateChange"></v-date-picker>
    </v-locale-provider>
</template>
